import React from "react";
import { Link } from "react-router-dom";
import { Anchor, Badge, Card, Group, Progress, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconArrowRight, type IconInbox } from "@tabler/icons-react";

export const tabular: React.CSSProperties = { fontVariantNumeric: "tabular-nums" };

/** Semantic colour per workflow status; the status text is always shown alongside it. */
export function statusColor(status: string): string {
  switch (status) {
    case "APPROVED":
    case "ACTIVE":
    case "AWARDED":
    case "COMPLETED":
    case "EVALUATION_COMPLETE":
      return "teal";
    case "REJECTED":
    case "TERMINATED":
    case "CANCELLED":
      return "red";
    case "PENDING":
    case "SUBMITTED":
    case "UNDER_REVIEW":
    case "UNDER_PEER_REVIEW":
    case "UNDER_HOP_REVIEW":
    case "PENDING_APPROVAL":
      return "orange";
    case "SCHEDULED":
    case "PUBLISHED":
    case "IN_PROGRESS":
    case "COLLECTION":
      return "blue";
    case "OPENED":
    case "TECH_EVAL":
    case "FIN_EVAL":
      return "indigo";
    case "EXPIRED":
      return "dark";
    default:
      return "gray";
  }
}

export function statusLabel(status: string): string {
  const s = status.replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="light" color={statusColor(status)} radius="sm" size="sm">
      {statusLabel(status)}
    </Badge>
  );
}

export function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="space-between" mb="md" wrap="nowrap">
        <Title order={2} size="h4">
          {title}
        </Title>
        {action && (
          <Anchor component={Link} to={action.to} size="sm" fw={500} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            <Group gap={4} wrap="nowrap">
              {action.label}
              <IconArrowRight size={14} aria-hidden />
            </Group>
          </Anchor>
        )}
      </Group>
      {children}
    </Card>
  );
}

export function EmptyState({ icon: Icon, message }: { icon: typeof IconInbox; message: string }) {
  return (
    <Stack align="center" gap="xs" py="lg">
      <ThemeIcon variant="light" color="gray" size={40} radius="xl">
        <Icon size={22} aria-hidden />
      </ThemeIcon>
      <Text size="sm" c="dimmed" ta="center">
        {message}
      </Text>
    </Stack>
  );
}

export type Kpi = {
  label: string;
  value: string;
  hint: string;
  to: string;
  icon: typeof IconInbox;
  color: string;
  progress?: number;
};

export function KpiTile({ kpi }: { kpi: Kpi }) {
  return (
    <Card
      component={Link}
      to={kpi.to}
      withBorder
      radius="md"
      padding="lg"
      className="transition-shadow duration-200 hover:shadow-md focus-visible:shadow-md"
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed" fw={500}>
          {kpi.label}
        </Text>
        <ThemeIcon variant="light" color={kpi.color} radius="md" size={34}>
          <kpi.icon size={18} aria-hidden />
        </ThemeIcon>
      </Group>
      <Text fz={28} fw={700} lh={1.2} mt={4} style={tabular}>
        {kpi.value}
      </Text>
      {kpi.progress !== undefined && (
        <Progress value={kpi.progress} color={kpi.color} size="sm" mt="sm" aria-label={`${kpi.label}: ${kpi.hint}`} />
      )}
      <Text size="xs" c="dimmed" mt={kpi.progress !== undefined ? 6 : 4}>
        {kpi.hint}
      </Text>
    </Card>
  );
}
